import CloseIcon from "@mui/icons-material/Close";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import React, { useContext, useRef, useState } from "react";
import { toast } from "react-toastify";
import CustomButton from "../Common/CustomButton";
import CustomToast from "../Common/CustomToast";
import { LoggedInSkeleton } from "../Common/LoggedInSkeleton";
import { getReconTopBarButtons } from "../Common/TopNavBar";
import useFetch from "../Common/useFetch";
import { userContext } from "../Contexts/userContext";
import { downloadExcelFromBuffer } from "./UploadFileCommonDialog";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReportTemplateConfig {
  id: string;
  label: string;
  description: string;
  downloadEndpoint: string;
  uploadEndpoint: string;
  downloadResultKey: string;
  fileName: string;
}

// The early-payment endpoints are scoped to the *anchor*, not the logged-in admin's actor.
// Same anchor id the rest of the operations screens use (OperationsPage, BulkActionsDialog).
const ANCHOR_ID = 2;

const getReportTemplates = (anchorId: number): ReportTemplateConfig[] => [
  {
    id: "amountUpdate",
    label: "Amount Update",
    description: "Download the amount update template.",
    downloadEndpoint: `/invoices/early-payment/${anchorId}/amount-update/template`,
    uploadEndpoint: `/invoices/early-payment/${anchorId}/amount-update/report`,
    downloadResultKey: "template",
    fileName: "amount-update-template.xlsx",
  },
];

// ─── Upload Dialog ─────────────────────────────────────────────────────────────

interface UploadTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  template: ReportTemplateConfig | null;
}

const UploadTemplateDialog: React.FC<UploadTemplateDialogProps> = ({ open, onClose, template }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const handleClose = () => {
    setErrorMessages([]);
    onClose();
  };

  const handleFileUpload = async (files: FileList) => {
    if (!template || files.length === 0) return;
    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", files[0]);

    await useFetch(template.uploadEndpoint, "POST", {
      data: formData,
      failureMessage: `Failed to upload ${template.label}`,
      thenCallBack: (res) => {
        const result = res.data?.result;
        toast.success(
          <CustomToast
            message={
              result?.success !== undefined
                ? `File uploaded successfully: ${result.success} out of ${
                    result.success + (result.failed || 0)
                  } records`
                : `${template.label} uploaded successfully`
            }
          />
        );
        if (result?.failed > 0) {
          setErrorMessages(result.errors?.map((e: any) => `${e.row}: ${e.error}`) || []);
        } else {
          setErrorMessages([]);
          handleClose();
        }
        setIsUploading(false);
      },
      catchCallBack: () => {
        setIsUploading(false);
      },
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length) handleFileUpload(files);
  };

  const triggerFileSelect = () => {
    if (!isUploading) fileInputRef.current?.click();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6">Upload — {template?.label}</Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 4, py: 3 }}>
        {/* Guidelines / Errors panel */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: errorMessages.length > 0 ? "#f8d7da" : "#e9f6fe",
            borderRadius: 2,
            p: 2,
            mb: 4,
            color: errorMessages.length > 0 ? "error.dark" : "primary.dark",
          }}
        >
          <Typography fontWeight={600} gutterBottom>
            {errorMessages.length > 0 ? "Errors in the uploaded file" : "Upload Guidelines"}
          </Typography>
          <List disablePadding>
            {(errorMessages.length > 0
              ? errorMessages
              : [
                  "Download the template first and fill in your data",
                  "Only XLS / XLSX / CSV formats are accepted",
                  "Duplicate entries will be updated with the latest data",
                  "Invalid or incomplete records will be flagged",
                ]
            ).map((text, i) => (
              <ListItem key={i} disableGutters sx={{ p: 0 }}>
                <ListItemText primary={`• ${text}`} />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Drop zone */}
        <Box
          onClick={triggerFileSelect}
          sx={{
            p: 6,
            border: "2px dashed #ccc",
            borderRadius: 2,
            textAlign: "center",
            color: "text.secondary",
            backgroundColor: "#fafafa",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 40, color: "#a94fc4", mb: 1 }} />
          <CustomButton
            sx={{ my: 2 }}
            disabled={isUploading}
            startIcon={isUploading ? <CircularProgress size={20} /> : null}
          >
            {isUploading ? "Uploading…" : `Upload ${template?.label}`}
          </CustomButton>
          <Typography variant="body2">XLS / XLSX / CSV formats supported</Typography>
        </Box>

        <input ref={fileInputRef} type="file" hidden accept=".xls,.xlsx,.csv" onChange={handleFileSelect} />
      </DialogContent>
    </Dialog>
  );
};

// ─── Template Card ─────────────────────────────────────────────────────────────

interface TemplateCardProps {
  config: ReportTemplateConfig;
  onUploadClick: (config: ReportTemplateConfig) => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({ config, onUploadClick }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = () => {
    setIsDownloading(true);
    useFetch(config.downloadEndpoint, "GET", {
      failureMessage: `Failed to download ${config.label} template`,
      thenCallBack: (response) => {
        const buffer = response.data?.result?.[config.downloadResultKey];
        if (buffer) {
          downloadExcelFromBuffer(buffer, config.fileName);
        } else {
          toast.info(<CustomToast message="Template downloaded (dummy response — no file content yet)" />);
        }
        setIsDownloading(false);
      },
      catchCallBack: () => {
        setIsDownloading(false);
      },
    });
  };

  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: 3,
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.08)" },
      }}
    >
      <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", p: 3 }}>
        {/* Icon */}
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            backgroundColor: "#f3e8f7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <CloudDownloadIcon sx={{ color: "#541C4C", fontSize: 26 }} />
        </Box>

        {/* Text */}
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="subtitle1" fontWeight={600} lineHeight={1.3}>
            {config.label}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {config.description}
          </Typography>
        </Box>

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
          <CustomButton
            variant="outlined"
            startIcon={isDownloading ? <CircularProgress size={16} /> : <CloudDownloadIcon />}
            disabled={isDownloading}
            onClick={handleDownload}
          >
            {isDownloading ? "Downloading…" : "Download Template"}
          </CustomButton>
          <CustomButton startIcon={<CloudUploadIcon />} onClick={() => onUploadClick(config)}>
            Upload
          </CustomButton>
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function ReportTemplates() {
  const { actor } = useContext(userContext);

  const [uploadTarget, setUploadTarget] = useState<ReportTemplateConfig | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const openUploadDialog = (config: ReportTemplateConfig) => {
    setUploadTarget(config);
    setUploadDialogOpen(true);
  };

  const closeUploadDialog = () => {
    setUploadDialogOpen(false);
    setUploadTarget(null);
  };

  return (
    <LoggedInSkeleton topBarButtons={getReconTopBarButtons("", "", {}, actor)}>
      <Box sx={{ maxWidth: 900, mx: "auto", py: 3, px: 1 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={700} color="#541C4C">
            Amount Update
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Download the amount update template.
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Template list */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {getReportTemplates(ANCHOR_ID).map((tpl) => (
            <TemplateCard key={tpl.id} config={tpl} onUploadClick={openUploadDialog} />
          ))}
        </Box>
      </Box>

      {/* Upload dialog */}
      <UploadTemplateDialog open={uploadDialogOpen} onClose={closeUploadDialog} template={uploadTarget} />
    </LoggedInSkeleton>
  );
}
